import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {marketT} from '@/lib/types/market.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderMarketTableStats} from '@/lib/config/marketTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useRetailer} from '@/pages/(shared)/retailers/detail/providers/retailerDetailsProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

/**
 * Retailer Market Detail Component
 * @param {retailerT} retailer - The Retailer object
 * @returns {JSX.Element}
 */
export const RetailerDetailMarkets = () => {
  const {toast} = useNotifications();
  const {isAdmin} = useAuth();
  const {retailer, searchMarketsToAdd, addRetailerToMarket, removeRetailerFromMarket} = useRetailer();
  const {data: stats} = useViewQuery({view: 'market_stats'});

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<marketT[]>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<marketT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Market_Retailer',
    title: 'Markets Summary',
    columns: ['fk_retailer_id', 'date_removed', ['Market', ['*']], ['Retailer', ['*']]],
    searchColumn: 'Market.market_name',
    filters: [{column: 'fk_retailer_id', operator: 'eq', value: retailer?.retailer_id}],
    addButtonLabel: 'Add Retailer to Market',
    noResultsMessage: 'No markets associated with retailer',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {
        header: 'Status',
        field: 'date_removed',
        render: row => (
          <span className={`badge bg-${!row.date_removed ? 'success' : 'danger'} text-white`}>
            {!row.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Market.market_name',
        render: row => (
          <Link to={`/markets/${row.Market.market_id}`} className="text-primary hover:text-primary dark:text-primary">
            {row.Market.market_name}
          </Link>
        ),
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Market;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderMarketTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchMarketsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (market: marketT) => {
    try {
      await addRetailerToMarket(market.market_id);
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Retailer To Market',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Market not found');

    try {
      await removeRetailerFromMarket(dataRowToDeleteRef.current.market_id);
      toast.success('Retailer removed from Market');
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Retailer From Market',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Markets"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Market Name', field: 'market_name'}],
          valueField: 'market_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this Retailer from the Market?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
